import { observable, action, toJS } from 'mobx';
import _ from 'lodash';
import { ConceptType, DeliveryChannelName, DeliveryState, DeliveryStatus } from 'enums/common';

export default class SidebarFilterStore {
  @observable chainIds: string[] = [];
  @observable query: string = '';
  @observable archived: boolean = false;
  @observable storeIds: string[] = [];
  @observable sort: string = 'delivery.id';
  @observable order: 'asc' | 'desc' = 'desc';
  @observable types: Record<string, boolean> = {};
  @observable channels: Record<string, boolean> = {};
  @observable statuses: Record<string, boolean> = {};
  @observable states: Record<string, boolean> = {};
  @observable startDateFrom: string = null;
  @observable startDateTo: string = null;
  @observable endDateFrom: string = null;
  @observable endDateTo: string = null;
  @observable deadlineFrom: string = null;
  @observable deadlineTo: string = null;

  @observable private initialSnapshot: Record<string, any>;
  private trackedFields = [
    'chainIds',
    'archived',
    'storeIds',
    'types',
    'channels',
    'statuses',
    'states',
    'endDateFrom',
    'endDateTo',
    'deadlineFrom',
    'deadlineTo',
    'query',
    'startDateFrom',
    'startDateTo',
  ];
  constructor() {
    this.types = this.initMapFromEnum(ConceptType);
    this.channels = this.initMapFromEnum(DeliveryChannelName);
    this.statuses = this.initMapFromEnum(DeliveryStatus);
    this.states = this.initMapFromEnum(DeliveryState);
    this.initialSnapshot = this.getSnapshotFor(this.trackedFields);
  }
  getSnapshotFor(fields: string[]): Record<string, any> {
    return _.pick(toJS(this), fields);
  }
  private initMapFromEnum<T extends { [key: string]: string }>(enumObj: T): Record<string, boolean> {
    return _.reduce(
      Object.keys(enumObj),
      (acc, key) => {
        const value = enumObj[key as keyof T];
        acc[value] = true;
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }

  @action setQuery(value: string) {
    this.query = value;
  }
  @action setChainIds(value: string[]) {
    this.chainIds = value;
  }

  @action setStoreIds(value: string[]) {
    this.storeIds = value;
  }
  @action setTypes(value: Record<string, boolean>) {
    this.types = value;
  }
  @action setSort(sort: string) {
    this.sort = sort;
  }
  @action setStates(states: Record<string, boolean>) {
    this.states = states;
  }
  @action setStatuses(statuses: Record<string, boolean>) {
    this.statuses = statuses;
  }
  @action setChannels(channels: Record<string, boolean>) {
    this.channels = channels;
  }
  @action setOrder(order: 'asc' | 'desc') {
    this.order = order;
  }
  @action setStartDateFrom(startDateFrom: string) {
    this.startDateFrom = startDateFrom;
  }
  @action setStartDateTo(startDateTo: string) {
    this.startDateTo = startDateTo;
  }
  @action setEndDateFrom(endDateFrom: string) {
    this.endDateFrom = endDateFrom;
  }
  @action setEndDateTo(endDateTo: string) {
    this.endDateTo = endDateTo;
  }
  @action setDeadlineFrom(deadlineFrom: string) {
    this.deadlineFrom = deadlineFrom;
  }
  @action setDeadlineTo(deadlineTo: string) {
    this.deadlineTo = deadlineTo;
  }
  @action reset(chainIdsReset: string[]) {
    this.chainIds = chainIdsReset;
    this.query = '';
    this.archived = false;
    this.storeIds = [];
    this.types = this.initMapFromEnum(ConceptType);
    this.channels = this.initMapFromEnum(DeliveryChannelName);
    this.statuses = this.initMapFromEnum(DeliveryStatus);
    this.states = this.initMapFromEnum(DeliveryState);
    this.startDateFrom = null;
    this.startDateTo = null;
    this.endDateFrom = null;
    this.endDateTo = null;
    this.deadlineFrom = null;
    this.deadlineTo = null;

    this.initialSnapshot = this.getSnapshotFor(this.trackedFields);
  }

  isDirty(fields: string[]): boolean {
    const current = this.getSnapshotFor(fields);
    return !_.isEqual(current, this.initialSnapshot);
  }
}
